import { useState } from 'react';
import { cn } from 'cn';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { IconRail } from '@/features/reader/IconRail';
import { useUrlParam } from '@/features/reader/useUrlState';
import { useStoredPreference } from '@/features/reader/useStoredPreference';
import { fetchLanguages, type Language } from '@/lib/languages';
import { SourceBadge, TranslationBadge, NoTranslationBadge } from '@/components/provenance-badge';
import type { TranslationReading } from '@/features/reader/types';
import { fetchTopic, MIN_TOPIC_LENGTH, type TopicMatch, type TopicResult } from './prepareApi';
import { buildNotes, notesOrigin } from './prepareNotes';

/**
 * Preparing from what the publishers have already said.
 *
 * **It finds; it does not write.** A topic goes in and published passages come
 * out, each with the khutbah it belongs to and a link into the reader. Nothing
 * on this screen is composed by a model, which is the only way a preparation
 * tool can exist inside a product whose first rule is never to fabricate
 * religious content. A model asked to write a khutbah from a topic produces
 * invented verses and plausible hadith with the same fluency as prose, and no
 * badge downstream could tell the two apart.
 *
 * **Matching is literal, and the screen says so.** Trigram containment over
 * the normalised Arabic and the published translations — it finds khutbahs
 * that *contain the words typed*, not khutbahs that are *about* the idea.
 * ADR 0010 §4 makes this the lexical half of a planned hybrid; the vector half
 * needs embeddings behind an AI provider key. Calling this "related topics"
 * before that exists would be a promise the code cannot keep, so the empty
 * state and the result count both describe it as word matching.
 *
 * **Every quotation leaves with its citation.** The copied notes carry, per
 * passage, the publisher, the Friday, the passage number, the translation's
 * provenance and a link back — and a standing line saying nothing in them was
 * written by the assistant. A quotation separated from its provenance is how
 * an unreviewed AI translation ends up in a sermon as though it were
 * authoritative.
 *
 * **The query key has no source version, and that is a deliberate limit.** A
 * search spans many khutbahs, so there is no single version to key on the way
 * the reader does. Results are therefore kept briefly and refetched on mount,
 * and the citation link carries only the khutbah id so the reader fetches the
 * current text itself. Copied notes are a snapshot by nature, which is exactly
 * why they carry the date and the link rather than standing alone.
 */

function formatFriday(isoDate: string): string {
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * The badge components speak `TranslationReading`; a search match carries the
 * three fields that decide the badge. Adapting here keeps one implementation
 * of "which badge" rather than a second opinion on this screen.
 */
function asTranslationReading(match: TopicMatch): TranslationReading | null {
  if (match.translationText === null || match.translationOrigin === null) {
    return null;
  }
  return {
    text: match.translationText,
    origin: match.translationOrigin,
    entryMethod: 'api',
    status: match.translationStatus ?? 'published',
    reviewed: match.translationReviewed,
    checked: false,
    modelId: null,
    declaredModel: null,
  };
}

function MatchRow({
  match,
  result,
  language,
  scriptClass,
  direction,
  selected,
  onToggle,
}: {
  match: TopicMatch;
  result: TopicResult;
  language: string;
  scriptClass: string;
  direction: 'ltr' | 'rtl';
  selected: boolean;
  onToggle: () => void;
}) {
  const translation = asTranslationReading(match);
  return (
    <li className="border-t border-border py-3 first:border-t-0">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Include passage ${match.ordinal + 1} in the notes`}
          className="mt-1 size-4 shrink-0 accent-primary"
        />
        <div className="min-w-0 flex-1">
          {/* The Arabic as published: its own direction and script, never
              justified, and nothing about it recoloured by this screen. */}
          <p lang="ar" dir="rtl" className="script-ar text-reading-foreground">
            {match.sourceText}
          </p>

          {match.translationText ? (
            <p
              lang={language}
              dir={direction}
              className={cn(scriptClass, 'mt-2 text-sm text-reading-foreground')}
            >
              {match.translationText}
            </p>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">
              Not translated into this language yet.
            </p>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>Passage {match.ordinal + 1}</span>
            {translation === null ? <NoTranslationBadge /> : <TranslationBadge translation={translation} />}
            {match.uncertain ? (
              <span className="rounded-sm bg-warning-bg px-2 py-0.5 text-warning">
                Lost text — could not be read reliably
              </span>
            ) : null}
            <a
              href={`?khutbah=${encodeURIComponent(result.khutbahId)}&language=${encodeURIComponent(language)}#passage-${match.passageId}`}
              className="text-primary underline"
            >
              Open in context
            </a>
          </div>
        </div>
      </div>
    </li>
  );
}

function ResultCard({
  result,
  language,
  scriptClass,
  direction,
  selected,
  onToggle,
  onToggleAll,
}: {
  result: TopicResult;
  language: string;
  scriptClass: string;
  direction: 'ltr' | 'rtl';
  selected: ReadonlySet<string>;
  onToggle: (passageId: string) => void;
  onToggleAll: (result: TopicResult, include: boolean) => void;
}) {
  const allSelected = result.matches.every((m) => selected.has(m.passageId));
  const hidden = result.matchCount - result.matches.length;

  return (
    <article className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>{formatFriday(result.friday)}</span>
            <span aria-hidden>·</span>
            <span>{result.institutionName}</span>
            <SourceBadge institutionKey={result.sourceKind === 'user' ? '' : result.institutionKey} />
          </div>
          {result.titleOriginal ? (
            <h2
              lang="ar"
              dir="rtl"
              className="script-ar mt-1.5 text-lg text-reading-foreground"
            >
              {result.titleOriginal}
            </h2>
          ) : (
            <h2 className="mt-1.5 text-base font-medium">
              Khutbah of {formatFriday(result.friday)}
            </h2>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            {result.matchCount === 1
              ? '1 passage contains these words'
              : `${result.matchCount} passages contain these words`}
            {hidden > 0 ? ` · ${hidden} more in the reader` : ''}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onToggleAll(result, !allSelected)}
          >
            {allSelected ? 'Clear these' : 'Take all shown'}
          </Button>
          <Button asChild variant="secondary" size="sm">
            <a href={`?khutbah=${encodeURIComponent(result.khutbahId)}&language=${encodeURIComponent(language)}`}>
              Read it
            </a>
          </Button>
        </div>
      </div>

      <ul className="mt-3">
        {result.matches.map((match) => (
          <MatchRow
            key={match.passageId}
            match={match}
            result={result}
            language={language}
            scriptClass={scriptClass}
            direction={direction}
            selected={selected.has(match.passageId)}
            onToggle={() => onToggle(match.passageId)}
          />
        ))}
      </ul>
    </article>
  );
}

export function PrepareScreen() {
  const [topic, setTopic] = useUrlParam('topic', '');
  const [language, setLanguage] = useStoredPreference(
    'mimbar.reader.language',
    'ml',
    (v): v is string => typeof v === 'string' && /^[a-z]{2,3}$/.test(v),
  );
  // What is in the box, which is not yet what has been searched for. The URL
  // changes on submit so that every search is a shareable link and the back
  // button steps through searches rather than keystrokes.
  const [draft, setDraft] = useState(topic);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [notes, setNotes] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const languages = useQuery({
    queryKey: ['languages', 'target'],
    queryFn: ({ signal }) => fetchLanguages('target', signal),
    staleTime: 60 * 60 * 1000,
  });

  const searchable = topic.trim().length >= MIN_TOPIC_LENGTH;
  const found = useQuery({
    queryKey: ['prepare', topic.trim(), language],
    queryFn: ({ signal }) => fetchTopic(topic.trim(), language, signal),
    enabled: searchable,
    // Short, and refetched on mount: a withdrawn khutbah has to leave these
    // results quickly, and there is no source version to key on here.
    staleTime: 30 * 1000,
    // Keep the previous answer on screen while the next one loads, rather
        // than replacing readable results with a skeleton.
    placeholderData: (previous) => previous,
  });

  const results = found.data ?? [];
  const reading: Language | null =
    languages.data?.find((entry) => entry.code === language) ?? null;
  const scriptClass = reading?.scriptClass ?? 'script-ml';
  const direction = reading?.direction ?? 'ltr';

  const toggle = (passageId: string) =>
    setSelected((was) => {
      const next = new Set(was);
      if (next.has(passageId)) next.delete(passageId);
      else next.add(passageId);
      return next;
    });

  const toggleAll = (result: TopicResult, include: boolean) =>
    setSelected((was) => {
      const next = new Set(was);
      for (const match of result.matches) {
        if (include) next.add(match.passageId);
        else next.delete(match.passageId);
      }
      return next;
    });

  const take = async () => {
    const text = buildNotes(notesOrigin(), topic.trim(), language, results, selected);
    setNotes(null);
    setCopied(false);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // Clipboard access is refused in plenty of ordinary situations — an
      // insecure origin, a denied permission, a browser that wants a gesture
      // it did not get. Showing the notes is the recovery, not an error
      // message: the work is done either way and the reader can select it.
      setNotes(text);
    }
  };

  const selectedCount = results
    .flatMap((r) => r.matches)
    .filter((m) => selected.has(m.passageId)).length;

  return (
    <div className="flex h-[calc(100dvh-var(--app-bar-height))] overflow-hidden">
      <IconRail />

      <main className="flex min-h-0 flex-col flex-1 overflow-y-auto px-4 py-8 sm:px-6">
        <div className="mx-auto w-full max-w-4xl">
          <h1 className="text-2xl font-semibold tracking-tight">
            Prepare from published khutbahs
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-pretty text-muted-foreground">
            Search the published khutbahs for a word, and take the passages that
            contain it with their citations. Everything here is quoted from a
            khutbah a ministry published — nothing on this screen is written for
            you.
          </p>

          <form
            className="mt-5 flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              setTopic(draft.trim());
              setSelected(new Set());
              setNotes(null);
              setCopied(false);
            }}
          >
            <div className="min-w-56 flex-1">
              <label htmlFor="topic" className="block text-xs font-medium text-muted-foreground">
                A word, as it appears in the khutbah or in your reading language
              </label>
              <input
                id="topic"
                type="search"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="صبر · ക്ഷമ · patience"
                className={cn(
                  'mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm',
                  'outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
                )}
              />
            </div>

            {/* shrink-0 so the label keeps its own width: at the widths where
                the row is tight it wrapped under the topic field and read as
                that field's label instead of this one's. */}
            <div className="shrink-0">
              <label htmlFor="prepare-language" className="block text-xs font-medium text-muted-foreground">
                Reading language
              </label>
              <select
                id="prepare-language"
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
                className="mt-1 h-10 rounded-md border border-input bg-background px-2 text-sm"
              >
                {(languages.data ?? []).map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {entry.nameEn}
                  </option>
                ))}
              </select>
            </div>

            <Button type="submit" size="lg">
              Search
            </Button>
          </form>

          {/* The honest description of what the search does. It sits with the
              control rather than in a footnote, because a reader who expects
              meaning-matching will read an empty result as "nothing was said
              about this", which would be false. */}
          <p className="mt-2 text-xs text-muted-foreground">
            This matches the words themselves, not their meaning — so a khutbah
            that discusses the idea in other words will not appear. Searching by
            meaning needs the retrieval that is not built yet.
          </p>

          {!searchable ? (
            <div className="mt-6 rounded-lg border border-dashed border-border p-6">
              <h2 className="text-base font-semibold">Type a word to begin</h2>
              <p className="mt-2 max-w-prose text-sm text-pretty text-muted-foreground">
                Arabic is matched without its diacritics, so{' '}
                <span lang="ar" dir="rtl" className="script-ar">
                  الصَّبْر
                </span>{' '}
                and{' '}
                <span lang="ar" dir="rtl" className="script-ar">
                  الصبر
                </span>{' '}
                find the same passages. Your own language is searched too, over
                the translations that have been published.
              </p>
            </div>
          ) : found.isPending ? (
            <div className="mt-6 flex flex-col gap-3" aria-busy>
              <Skeleton className="h-28 w-full" />
              <Skeleton className="h-28 w-full" />
            </div>
          ) : found.isError ? (
            <div className="mt-6 rounded-lg border border-destructive p-6">
              <h2 className="text-base font-semibold">The search could not run</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {found.error instanceof Error
                  ? found.error.message
                  : 'The server did not answer.'}
              </p>
            </div>
          ) : results.length === 0 ? (
            <div className="mt-6 rounded-lg border border-dashed border-border p-6">
              <h2 className="text-base font-semibold">
                No published khutbah contains that word
              </h2>
              <p className="mt-2 max-w-prose text-sm text-pretty text-muted-foreground">
                That is a statement about these words, not about the subject.
                Try the word as it would appear in the khutbah, or search the
                Arabic directly.
              </p>
            </div>
          ) : (
            <>
              <div
                aria-live="polite"
                className="mt-6 flex flex-wrap items-center justify-between gap-3"
              >
                <p className="text-sm text-muted-foreground">
                  {results.length === 1
                    ? '1 khutbah contains it'
                    : `${results.length} khutbahs contain it`}
                  {found.isFetching ? ' · refreshing' : ''}
                </p>
                <div className="flex items-center gap-2">
                  {copied ? (
                    <span className="text-xs text-success">Copied with citations</span>
                  ) : null}
                  <Button
                    type="button"
                    size="lg"
                    disabled={selectedCount === 0}
                    // The handler must return void, not a promise: a
                    // rejected one here would be an unhandled rejection, and
                    // `take` already handles its own failure.
                    onClick={() => void take()}
                  >
                    {selectedCount === 0
                      ? 'Select passages to take'
                      : `Copy ${selectedCount} with citations`}
                  </Button>
                </div>
              </div>

              {notes !== null ? (
                <div className="mt-3 rounded-lg border border-border p-3">
                  <p className="text-xs text-muted-foreground">
                    Copying was blocked by the browser. The notes are below —
                    select and copy them.
                  </p>
                  <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-xs">
                    {notes}
                  </pre>
                </div>
              ) : null}

              <div className="mt-4 flex flex-col gap-4 pb-12">
                {results.map((result) => (
                  <ResultCard
                    key={result.khutbahId}
                    result={result}
                    language={language}
                    scriptClass={scriptClass}
                    direction={direction}
                    selected={selected}
                    onToggle={toggle}
                    onToggleAll={toggleAll}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
